import React from "react";

import { ModuleContextProvider } from "@brexhq/next/src/contexts/ModuleContext";
import { ModuleLayoutProps } from "@brexhq/shared/types/modules/ModuleLayout";
import { isDev } from "@brexhq/shared/utils/env/isDevEnv";

import { ModuleWrappers } from "./ComponentDictionary";
import PageBuilderErrorBoundary from "./PageBuilderErrorBoundary";

const PageBuilder: React.FC<{
  blocks: any;
  components: ModuleWrappers;
}> = ({ blocks: blocksWithSnippets, components }) => {
  if (!blocksWithSnippets) return null;

  // If any blocks are "Core Page Builder Snippets", get the core page content and spread it into the array.
  const blocks = blocksWithSnippets.flatMap((block) => {
    if (block?._type === "corePageSnippetModule") {
      return block.corePageSnippetReference.corePageBuilder;
    }
    return block;
  });

  let desktop = 0;
  let tablet = 0;
  let mobile = 0;
  let overall = 0;

  const updateBreakpointIndices = (
    breakpoints: ModuleLayoutProps["breakpointVisibility"],
  ) => {
    if (breakpoints?.showDesktop) {
      desktop += 1;
    }
    if (breakpoints?.showTablet) {
      tablet += 1;
    }
    if (breakpoints?.showMobile) {
      mobile += 1;
    }
    overall += 1;
  };
  const getBreakPointIndices = (
    breakpoints: ModuleLayoutProps["breakpointVisibility"],
  ) => {
    return {
      desktop: breakpoints?.showDesktop ? desktop : undefined,
      tablet: breakpoints?.showTablet ? tablet : undefined,
      mobile: breakpoints?.showMobile ? mobile : undefined,
      overall,
    };
  };

  return (
    <section id="pageBuilder">
      {blocks.map((block: any, index: number) => {
        let key = index;
        if (!block && isDev) {
          return (
            <h1 key={key} style={{ color: "black" }}>
              Empty block encountered
            </h1>
          );
        }

        updateBreakpointIndices(block?.moduleLayout?.breakpointVisibility);

        const Component = components[block._type];

        if ("_key" in block) {
          key = block._key;
        } else {
          isDev &&
            console.warn("Missing gql key for block of type " + block._type);
        }

        if (Component)
          return (
            <PageBuilderErrorBoundary key={key} componentType={block._type}>
              <ModuleContextProvider
                indexInPageBuilder={getBreakPointIndices(
                  block?.moduleLayout?.breakpointVisibility,
                )}
                type={block._type}
              >
                <Component data={block} />
              </ModuleContextProvider>
            </PageBuilderErrorBoundary>
          );
        if (isDev)
          return (
            <h1 key={key} style={{ color: "black" }}>
              No renderer for {block._type}
            </h1>
          );
        return null;
      })}
    </section>
  );
};

export default PageBuilder;
